/**
 * Brand Configuration System
 */

export interface BrandConfig {
  brandName: string;
  className: string;
  supportLink: string;
  termsUrl: string;
  logoUrl?: string;
}

/**
 * Base configuration (Datapay default)
 */
const BASE_CONFIG: BrandConfig = {
  brandName: "Datapay",
  className: "brand-datapay",
  supportLink: "mailto:datapaysupportteam@datacom.co.nz",
  termsUrl:
    "https://digital.datacom.com/Datapay-Payroll-Assistant-FAQ-and-Terms-of-Use.html",
} as const;

/**
 * Brand-specific overrides - strongly typed with as const
 */
const BRAND_OVERRIDES = {
  smartly: {
    brandName: "Smartly",
    className: "brand-smartly",
    // Relative path so the logo resolves against <base href> for any
    // path-prefixed deployment (e.g. preview at /<pr-number>/).
    logoUrl: "smartly-logo.png",
  },
  // Add more brand overrides here as needed
} as const satisfies Record<string, Partial<BrandConfig>>;

// Strongly typed brand names derived from BRAND_OVERRIDES
export type BrandName = keyof typeof BRAND_OVERRIDES;

// All possible brand keys including implicit 'datapay' (base)
export type BrandKey = BrandName | "datapay" | null;

/**
 * Gets the complete brand configuration for a given brand key
 */
export function getBrandConfigForSubdomain(brandKey: BrandKey): BrandConfig {
  if (brandKey && brandKey in BRAND_OVERRIDES) {
    const overrides = BRAND_OVERRIDES[brandKey as BrandName];
    return {
      ...BASE_CONFIG,
      ...overrides,
    };
  }
  return BASE_CONFIG;
}

/**
 * Gets all available brand override keys (excludes implicit 'datapay')
 */
export function getAllBrandKeys(): readonly BrandName[] {
  return Object.keys(BRAND_OVERRIDES) as BrandName[];
}

/**
 * Gets all possible brand keys including 'datapay'
 */
export function getAllBrandKeysWithBase(): readonly (BrandName | "datapay")[] {
  return ["datapay", ...getAllBrandKeys()];
}

/**
 * Type guard to check if a string is a valid brand name
 */
export function isBrandName(value: string): value is BrandName {
  return value in BRAND_OVERRIDES;
}

/**
 * Type guard to check if a string is a valid brand key
 */
export function isBrandKey(value: string | null): value is BrandKey {
  return value === null || value === "datapay" || isBrandName(value);
}
